import {
  BOT_CATALOG,
  botById,
  rateLimitTarget,
  type ConfigScopeRef,
  type ServerWebRules,
  type WebCapabilities,
  type WebRuleChange,
  type WebRulesLayer,
} from '@bartizo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { changeWebRules } from '../api/traffic.js';
import { PatternBlockDialog, type BotRuleServer } from './BotRuleDialog.js';
import { CATEGORY_LABELS } from '../lib/traffic.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { ConfirmDialog } from './ConfirmDialog.js';

/**
 * The bot rules one server enforces, and where each was set (section 12; P6.6):
 * a rule set for every server is lifted for every server, so each entry names its
 * layer and its Remove button changes that layer. Beside them, what this server's
 * web layer can do at all — the same capabilities that decide which buttons the
 * Traffic page shows.
 */

const IP_BAN_WORDS: Readonly<Record<WebCapabilities['webIpBan'], string>> = {
  firewall: 'by the firewall (visitors connect directly)',
  nginx_bouncer: 'by CrowdSec’s Nginx bouncer',
  apache_bouncer: 'by CrowdSec’s Apache bouncer',
  cloudflare: 'at Cloudflare',
  none: 'not for website visitors: behind a proxy, a firewall ban only ever sees the proxy',
};

const botName = (id: string): string => botById(BOT_CATALOG, id)?.name ?? id;

function layerLabel(layer: WebRulesLayer): string {
  if (layer.scope === 'global') return 'Every server';
  if (layer.scope === 'group') return `Group ${layer.name ?? ''}`.trim();
  return 'This server';
}

function refOf(layer: WebRulesLayer): ConfigScopeRef {
  if (layer.scope === 'global' || layer.id === null) return { scope: 'global' };
  return layer.scope === 'group'
    ? { scope: 'group', id: layer.id }
    : { scope: 'server', id: layer.id };
}

interface Entry {
  label: string;
  change: WebRuleChange;
}

function entriesOf(layer: WebRulesLayer): Entry[] {
  const { rules } = layer;
  return [
    ...(rules.blocked_bots ?? []).map((id) => ({
      label: `Blocked: ${botName(id)}`,
      change: { kind: 'unblock' as const, bot: id },
    })),
    ...(rules.blocked_patterns ?? []).map((pattern) => ({
      label: `Blocked: user agents containing “${pattern}”`,
      change: { kind: 'unblock_pattern' as const, pattern },
    })),
    ...(rules.allowed_bots ?? []).map((id) => ({
      label: `Always allowed: ${botName(id)}`,
      change: { kind: 'disallow' as const, bot: id },
    })),
    ...(rules.rate_limits ?? []).map((rule) => {
      const target = rateLimitTarget(rule.match);
      const what =
        target.kind === 'bot'
          ? botName(target.id)
          : `every ${CATEGORY_LABELS[target.category].toLowerCase()}`;
      return {
        label: `Limited: ${what}, ${String(rule.per_minute)} a minute (burst ${String(rule.burst)})`,
        change: { kind: 'remove_rate_limit' as const, match: rule.match },
      };
    }),
  ];
}

export function BotRulesCard({
  server,
  rules,
  capabilities,
  canChange,
}: {
  server: BotRuleServer;
  rules: ServerWebRules;
  capabilities: WebCapabilities | undefined;
  canChange: boolean;
}): ReactNode {
  const serverId = server.id;
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<{ layer: WebRulesLayer; entry: Entry }>();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (target: { layer: WebRulesLayer; entry: Entry }) =>
      changeWebRules({ scope: refOf(target.layer), change: target.entry.change }),
    onSuccess: async () => {
      setRemoving(undefined);
      await queryClient.invalidateQueries({ queryKey: ['web-rules', serverId] });
    },
    onError: (caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'Could not lift the rule.');
    },
  });

  const layers = rules.layers
    .map((layer) => ({ layer, entries: entriesOf(layer) }))
    .filter((each) => each.entries.length > 0);

  return (
    <Card
      title="Bot rules"
      description="What this server’s web servers are told to block, limit and let through, and where each rule was set."
      {...(canChange && capabilities?.uaBlock === true
        ? {
            aside: (
              <Button
                variant="ghost"
                onClick={() => {
                  setAdding(true);
                }}
              >
                Block a user agent…
              </Button>
            ),
          }
        : {})}
    >
      {capabilities === undefined ? (
        <p className="muted text-sm">
          No rule applies here yet: they reach a server once its web environment is confirmed on its
          page.
        </p>
      ) : (
        <ul className="plain-list text-sm">
          <li>
            Blocking by user agent:{' '}
            {capabilities.uaBlock
              ? 'yes'
              : 'not yet — tick the sites Bartizo may add its line to in the web environment'}
          </li>
          <li>
            Rate limits:{' '}
            {capabilities.rateLimit === 'none' ? 'not on this web server' : 'yes, by Nginx'}
          </li>
          <li>IP bans reach the website {IP_BAN_WORDS[capabilities.webIpBan]}</li>
        </ul>
      )}

      {layers.length === 0 ? (
        <p className="muted text-sm">No bot is blocked, limited or singled out.</p>
      ) : (
        layers.map(({ layer, entries }) => (
          <section key={`${layer.scope}-${layer.id ?? ''}`} className="stack">
            <h3 className="text-sm">{layerLabel(layer)}</h3>
            <ul className="plain-list">
              {entries.map((entry) => (
                <li key={entry.label} className="row">
                  <span>{entry.label}</span>
                  {canChange && (
                    <Button
                      variant="ghost"
                      className="push-right"
                      onClick={() => {
                        setError(null);
                        setRemoving({ layer, entry });
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      {adding && (
        <PatternBlockDialog
          server={server}
          onClose={() => {
            setAdding(false);
          }}
        />
      )}

      {removing !== undefined && (
        <ConfirmDialog
          title="Lift a bot rule"
          description={`${removing.entry.label}, set for ${layerLabel(removing.layer).toLowerCase()}. Every server that layer reaches stops applying it.`}
          confirmLabel="Lift it"
          busy={remove.isPending}
          error={error}
          onConfirm={() => {
            remove.mutate(removing);
          }}
          onCancel={() => {
            setRemoving(undefined);
          }}
        />
      )}
    </Card>
  );
}
